import React, { useContext, useRef, useMemo, useEffect } from 'react'
import type { QuoteDocument } from 'paintscout'
import {
  createStyles,
  withStyles,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogStackContext,
  IconButton,
  Button,
  Editor,
  ConfirmationDialog,
  OverridableTextArea,
  useClientOptions,
  OverridableInputField,
  Grid
} from '@paintscout/ui'
import type { DialogProps, StyleClasses, MuiTheme as Theme } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import { getDetails, setDetails } from '@paintscout/util/builder'
import type { QuoteDetails } from '@paintscout/util/builder'
import { isLooselyEqual } from '@paintscout/util'
import { useQuote } from '../../context'

export interface EditCrewNoteDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  quote: QuoteDocument
  onConfirm: (quote: QuoteDocument) => void
  onCancel: () => void
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    dialogContent: {},
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    productDescriptionSection: {
      marginTop: theme.spacing(3)
    }
  })
}

function EditCrewNoteDialog(props: EditCrewNoteDialogProps) {
  const { quote, loading, onConfirm, onCancel, classes, ...otherProps } = props
  const { openDialog, dismissDialog, dismissAllDialogs } = useContext(DialogStackContext)
  const { isEditable } = useQuote()
  const { options } = useClientOptions()

  const { totalHours, crewNote, productDescription, details } = useMemo(() => {
    const details = getDetails({ quote, options })
    return {
      details,
      totalHours: details.totals.hours,
      crewNote: details.notes?.internal,
      productDescription: details.notes?.products
    }
  }, [])

  const crewNoteRef = useRef(null)
  const productDescriptionRef = useRef(null)
  const totalHoursRef = useRef(null)

  // Capture initial values from refs for comparison
  const initialValuesRef = useRef<{
    internal: string
    products: any
    hours: any
  } | null>(null)

  // Capture initial values after a short delay to ensure refs are initialized
  useEffect(() => {
    const timer = setTimeout(() => {
      if (crewNoteRef.current && productDescriptionRef.current && totalHoursRef.current && !initialValuesRef.current) {
        try {
          const initialInternal = crewNoteRef.current.getHTML() || ''
          const initialProducts = productDescriptionRef.current.getValue() || { default: '', useCustom: false }
          const initialHours = totalHoursRef.current.getValue() || { default: 0, useCustom: false, custom: 0 }

          initialValuesRef.current = {
            internal: normalizeHTML(initialInternal),
            products: normalizeProductsForComparison(initialProducts),
            hours: normalizeHoursForComparison(initialHours)
          }
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          // If we can't capture initial values, that's okay - we'll capture them on first check
        }
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  return (
    <Dialog onClose={handleCancel} fullWidth={true} maxWidth={'md'} {...otherProps}>
      <DialogTitle
        loading={loading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={handleCancel} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        Edit Work Order
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <OverridableInputField
              label="Total Hours"
              value={totalHours}
              format={'hours'}
              resetBlank={true}
              autoSelect={true}
              ref={totalHoursRef}
              disabled={!isEditable}
            />
          </Grid>
          <Grid item xs={12}>
            <Editor
              classes={{ editorContent: 'crew-note-text-field' }}
              fullWidth={true}
              content={crewNote}
              label={'Crew Note'}
              ref={crewNoteRef}
            />
          </Grid>
          <Grid item xs={12} className={classes.productDescriptionSection}>
            <OverridableTextArea
              classes={{ editorContent: 'crew-note-product-description' }}
              allowFormatting
              disabled={!isEditable}
              label={'Product Description'}
              value={productDescription}
              ref={productDescriptionRef}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleConfirm} variant={'contained'}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleConfirm(_ev: React.MouseEvent) {
    // Only update if we have changes
    if (isDirty()) {
      onConfirm(
        setDetails({
          quote,
          options,
          details: getUpdatedDetails()
        })
      )
    } else {
      onCancel()
    }
  }

  function handleCancel(_ev: React.MouseEvent) {
    // Only show confirmation dialog if there are actual changes
    const hasChanges = isDirty()
    if (!hasChanges) {
      return onCancel()
    }

    openDialog(ConfirmationDialog, {
      message: 'Any unsaved changes will be lost.',
      onConfirm: (_event: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        onCancel()
        dismissAllDialogs()
      },
      onCancel: (_event: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function isDirty() {
    try {
      // Get current values from refs - handle cases where refs might not be ready
      if (!crewNoteRef.current || !productDescriptionRef.current || !totalHoursRef.current) {
        return false
      }

      // Capture initial values if we haven't yet (lazy initialization)
      if (!initialValuesRef.current) {
        try {
          const initialInternal = crewNoteRef.current.getHTML() || ''
          const initialProducts = productDescriptionRef.current.getValue() || { default: '', useCustom: false }
          const initialHours = totalHoursRef.current.getValue() || { default: 0, useCustom: false, custom: 0 }

          initialValuesRef.current = {
            internal: normalizeHTML(initialInternal),
            products: normalizeProductsForComparison(initialProducts),
            hours: normalizeHoursForComparison(initialHours)
          }
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          // If we can't capture initial values, assume no changes
          return false
        }
      }

      const currentInternal = normalizeHTML(crewNoteRef.current.getHTML() || '')
      const currentProducts = normalizeProductsForComparison(
        productDescriptionRef.current.getValue() || { default: '', useCustom: false }
      )
      const currentHours = normalizeHoursForComparison(
        totalHoursRef.current.getValue() || { default: 0, useCustom: false, custom: 0 }
      )

      // Compare against initial captured values (not against details, which might have different format)
      const internalChanged = currentInternal !== initialValuesRef.current.internal
      const productsChanged = !isLooselyEqual(currentProducts, initialValuesRef.current.products)
      const hoursChanged = !isLooselyEqual(currentHours, initialValuesRef.current.hours)

      return internalChanged || productsChanged || hoursChanged
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      // If we can't determine if dirty, assume no changes to be safe
      return false
    }
  }

  function normalizeProductsForComparison(products: any): any {
    if (!products) {
      return { default: '', useCustom: false, custom: '' }
    }
    const normalized = {
      default: normalizeHTML(String(products.default || '')),
      useCustom: Boolean(products.useCustom),
      custom: normalizeHTML(String(products.custom || ''))
    }
    // If not using custom, ensure custom is empty
    if (!normalized.useCustom) {
      normalized.custom = ''
    }
    return normalized
  }

  function normalizeHoursForComparison(hours: any): any {
    if (!hours) {
      return { default: 0, useCustom: false, custom: 0 }
    }
    const normalized = {
      default: Number(hours.default || 0),
      useCustom: Boolean(hours.useCustom),
      custom: Number(hours.custom || 0)
    }
    // If not using custom, ensure custom matches default
    if (!normalized.useCustom) {
      normalized.custom = normalized.default
    }
    return normalized
  }

  function normalizeHTML(html: string | undefined | null): string {
    if (!html) return ''
    const trimmed = String(html).trim()
    // Normalize empty HTML variations
    if (
      trimmed === '' ||
      trimmed === '<p></p>' ||
      trimmed === '<p><br></p>' ||
      trimmed === '<br>' ||
      trimmed === '<p><br/></p>'
    ) {
      return ''
    }
    return trimmed
  }

  function getUpdatedDetails(): QuoteDetails {
    const internal = crewNoteRef.current?.getHTML() || ''
    let products = productDescriptionRef.current?.getValue() || { default: '', useCustom: false }
    const hours = totalHoursRef.current?.getValue() || { default: 0, useCustom: false, custom: 0 }

    // If our 'custom' is blank but our default is also empty,
    //set it to truly empty default so updates happen automaticaly
    if ((products.custom === '<p></p>' && products.default === '') || products.default === products.custom) {
      // Need to make sure the fix doesnt make it dirty, so reset to what it was
      if (
        details.notes.products.default === '' &&
        !details.notes.products.custom &&
        !details.notes.products.useCustom
      ) {
        delete products.custom
        delete products.useCustom
      } else {
        products = {
          ...products,
          custom: '',
          useCustom: false
        }
      }
    }

    return {
      ...details,
      notes: {
        ...details.notes,
        internal,
        products
      },
      totals: {
        ...details.totals,
        hours
      }
    }
  }
}

export default withStyles(styles)(EditCrewNoteDialog)
